import React from "react"
import PropTypes from "prop-types"
import dayjs from "dayjs"
import { Checkbox, ConfigProvider, DatePicker, Flex, InputNumber, Radio, Select } from "antd"
import { useTranslation } from "react-i18next"
import { baseTheme } from "themes/base"
import { Text } from "ui/Typography"
import { Icon } from "ui/Icon"
import { MAX_REPEAT_INTERVAL, REPEAT_FREQUENCIES, WEEKDAY_KEYS } from "helpers/blackouts"
import { DATE_DISPLAY_FORMAT_WITH_YEAR, DATE_VALUE_FORMAT } from "../../../constants"
import { RECURRENCE_ERRORS, createDefaultRecurrence, formatOccurrenceDate, getFirstEntryDate, getRecurrencePreview } from "../../../utils"
import styles from "./index.module.css"

/**
 * The unit dropdown in "Repeat every [N] [unit]" — it *is* the frequency picker, so the number and
 * what it counts read as one phrase. Singular and plural follow the number, picked by hand as in
 * `formatRecurrence`.
 */
const FREQUENCY_UNITS = {
  [REPEAT_FREQUENCIES.DAILY]: { one: ["blackout_repeat.unit_day", "day"], many: ["blackout_repeat.unit_days", "days"] },
  [REPEAT_FREQUENCIES.WEEKLY]: { one: ["blackout_repeat.unit_week", "week"], many: ["blackout_repeat.unit_weeks", "weeks"] },
  [REPEAT_FREQUENCIES.MONTHLY]: { one: ["blackout_repeat.unit_month", "month"], many: ["blackout_repeat.unit_months", "months"] },
  [REPEAT_FREQUENCIES.YEARLY]: { one: ["blackout_repeat.unit_year", "year"], many: ["blackout_repeat.unit_years", "years"] }
}

const ERROR_COPY = {
  [RECURRENCE_ERRORS.INTERVAL]: { key: "blackout_repeat.error_interval", fallback: "Enter a number from 1 to {{max}}" },
  [RECURRENCE_ERRORS.NO_WEEKDAYS]: { key: "blackout_repeat.error_no_weekdays", fallback: "Select at least one day" },
  [RECURRENCE_ERRORS.ENDS_BEFORE_START]: {
    key: "blackout_repeat.error_ends_before_start",
    fallback: "End date must be after the blackout's first date"
  }
}

const ENDS = { NEVER: "never", ON: "on" }

/**
 * The app theme sizes Input, Select and DatePicker but not InputNumber, which falls back to antd's
 * default and sits shorter than the unit Select beside it. Scoped to this row: changing the global
 * theme would resize every number box in the app.
 */
const SENTENCE_THEME = { components: { InputNumber: { controlHeight: baseTheme.token?.Select?.controlHeight } } }

/**
 * "Repeat" for a blackout, replacing the old "Repeat each year" checkbox.
 *
 * Unticked, the blackout happens once (`recurrence` null). Ticked, the rule is one sentence —
 * "Repeat every [2] [weeks]" — plus the weekdays for a weekly rule and when it stops, with the
 * whole rule read back underneath. The rule repeats every "Applies on" row; the server expands it, the same way it already
 * expanded the yearly flag.
 */
const RepeatSettings = ({ recurrence, onChange, entries, error }) => {
  const { t } = useTranslation()
  const isRepeating = Boolean(recurrence)
  const firstDate = getFirstEntryDate(entries)

  const update = (changes) => onChange({ ...recurrence, ...changes })

  const handleToggle = (event) => onChange(event.target.checked ? createDefaultRecurrence(entries) : null)

  const handleFrequencyChange = (frequency) => {
    // Weekly with no day picked would be invalid straight away; start from the first date's weekday.
    const needsDefaultDays = frequency === REPEAT_FREQUENCIES.WEEKLY && !recurrence.weekdays.length
    const weekdays = needsDefaultDays ? createDefaultRecurrence(entries).weekdays : recurrence.weekdays
    update({ frequency, weekdays })
  }

  const toggleWeekday = (day) => {
    const weekdays = recurrence.weekdays.includes(day) ? recurrence.weekdays.filter((item) => item !== day) : [...recurrence.weekdays, day]
    update({ weekdays })
  }

  /**
   * "On" is selected by `ends_on` holding a date, so choosing it must always produce one — a month
   * after the repeat starts (or after today, before any date is picked). Seeding it from the first
   * date alone left `ends_on` null when no date was picked yet, and the radio snapped back to Never.
   */
  const handleEndsChange = (event) => {
    if (event.target.value === ENDS.NEVER) {
      update({ ends_on: null })
      return
    }
    const startsFrom = firstDate ? dayjs(firstDate) : dayjs()
    update({ ends_on: startsFrom.add(1, "month").format(DATE_VALUE_FORMAT) })
  }

  // Never before today, and strictly after the repeat starts — ending on the first date itself
  // would leave nothing to repeat (see `getRecurrenceError`).
  const disableEndDate = (current) => current?.isBefore(dayjs().startOf("day")) || (firstDate && !current?.isAfter(dayjs(firstDate), "day"))

  const unitOptions = Object.entries(FREQUENCY_UNITS).map(([frequency, unit]) => {
    const [key, fallback] = recurrence?.interval > 1 ? unit.many : unit.one
    return { value: frequency, label: t(key, { defaultValue: fallback }) }
  })

  return (
    <div className={styles.wrapper}>
      <Checkbox checked={isRepeating} onChange={handleToggle} className={styles.toggle}>
        {t("blackout_repeat.repeat", { defaultValue: "Repeat" })}
      </Checkbox>

      {isRepeating && (
        <Flex vertical gap="var(--spacing-m)" className={styles.panel}>
          <Flex align="center" gap="var(--spacing-xs)" wrap="wrap">
            <Text variant="sub" size="md" fw="500" className={styles.label}>
              {t("blackout_repeat.repeat_every", { defaultValue: "Repeat every" })}
            </Text>
            <ConfigProvider theme={SENTENCE_THEME}>
              <InputNumber
              value={recurrence.interval}
              min={1}
              max={MAX_REPEAT_INTERVAL}
              precision={0}
              onChange={(value) => update({ interval: value })}
              aria-label={t("blackout_repeat.interval_label", { defaultValue: "How many" })}
              className={styles.interval}
              />
            </ConfigProvider>
            <Select
              value={recurrence.frequency}
              onChange={handleFrequencyChange}
              options={unitOptions}
              aria-label={t("blackout_repeat.unit_label", { defaultValue: "Repeat unit" })}
              className={styles.unitSelect}
            />
          </Flex>

          {recurrence.frequency === REPEAT_FREQUENCIES.WEEKLY && (
            <Flex vertical gap="var(--spacing-xxs)">
              <Text variant="sub" size="md" fw="500" className={styles.label}>
                {t("blackout_repeat.on_days", { defaultValue: "On" })}
              </Text>
              <Flex gap="var(--spacing-xs)" wrap="wrap" role="group">
                {WEEKDAY_KEYS.map((day, index) => {
                  const isSelected = recurrence.weekdays.includes(day)
                  return (
                    <button
                      key={day}
                      type="button"
                      aria-pressed={isSelected}
                      className={`${styles.dayPill} ${isSelected ? styles.dayPillActive : ""}`}
                      onClick={() => toggleWeekday(day)}
                    >
                      {dayjs().day(index).format("ddd")}
                    </button>
                  )
                })}
              </Flex>
            </Flex>
          )}

          {recurrence.frequency === REPEAT_FREQUENCIES.MONTHLY && firstDate && (
            <Text variant="caption" size="lg" className={styles.hint}>
              {t("blackout_repeat.monthly_hint", {
                defaultValue: "On day {{day}} of the month. Months without that day are skipped.",
                day: dayjs(firstDate).date()
              })}
            </Text>
          )}

          <Flex vertical gap="var(--spacing-xxs)">
            <Text variant="sub" size="md" fw="500" className={styles.label}>
              {t("blackout_repeat.ends", { defaultValue: "Ends" })}
            </Text>
            <Radio.Group value={recurrence.ends_on ? ENDS.ON : ENDS.NEVER} onChange={handleEndsChange}>
              <Flex align="center" gap="var(--spacing-m)" wrap="wrap">
                <Radio value={ENDS.NEVER}>{t("blackout_repeat.ends_never", { defaultValue: "Never" })}</Radio>
                <Radio value={ENDS.ON}>{t("blackout_repeat.ends_on", { defaultValue: "On" })}</Radio>
                {recurrence.ends_on && (
                  <DatePicker
                    value={dayjs(recurrence.ends_on)}
                    onChange={(date) => update({ ends_on: date ? date.format(DATE_VALUE_FORMAT) : null })}
                    disabledDate={disableEndDate}
                    format={DATE_DISPLAY_FORMAT_WITH_YEAR}
                    allowClear={false}
                    className={styles.endDate}
                  />
                )}
              </Flex>
            </Radio.Group>
          </Flex>

          {!error && <RepeatPreview recurrence={recurrence} entries={entries} />}

          {error && (
            <Text variant="caption" size="lg" className={styles.error}>
              {t(ERROR_COPY[error].key, { defaultValue: ERROR_COPY[error].fallback, max: MAX_REPEAT_INTERVAL })}
            </Text>
          )}
        </Flex>
      )}
    </div>
  )
}

/**
 * "What will happen", in two lines: how many times it repeats and when it stops, then the next
 * few dates. The rule itself is already on screen above it, so it is not restated — the dates are
 * what the guide cannot see otherwise, including the ones a rule quietly drops (a weekly rule
 * leaving out the start date's own weekday, a monthly one on the 31st skipping April).
 */
const RepeatPreview = ({ recurrence, entries }) => {
  const { t } = useTranslation()
  const preview = getRecurrencePreview(recurrence, entries)

  if (!preview) {
    return (
      <Flex align="center" gap="var(--spacing-xs)" className={styles.preview}>
        <Icon name="Info" width={16} height={16} />
        <Text variant="caption" size="lg" className={styles.previewText}>
          {t("blackout_repeat.preview_no_date", { defaultValue: "Pick a date under Applies on to see the dates this repeats on." })}
        </Text>
      </Flex>
    )
  }

  const isNone = preview.total === 0
  const countLine = (() => {
    if (preview.total === null) return t("blackout_repeat.preview_never", { defaultValue: "Repeats with no end date." })
    if (isNone) return t("blackout_repeat.preview_none", { defaultValue: "No dates match this rule. Check the days and the end date." })
    if (preview.isTotalCapped) {
      return t("blackout_repeat.preview_total_capped", { defaultValue: "Repeats more than {{count}} times.", count: preview.total })
    }
    if (preview.total === 1) {
      return t("blackout_repeat.preview_total_one", { defaultValue: "Happens once, on {{date}}.", date: formatOccurrenceDate(preview.last) })
    }
    return t("blackout_repeat.preview_total", {
      defaultValue: "Repeats {{count}} times, last on {{date}}.",
      count: preview.total,
      date: formatOccurrenceDate(preview.last)
    })
  })()
  const hasMore = preview.total === null || preview.total > preview.next.length

  return (
    <Flex vertical gap="var(--spacing-xxs)" className={styles.preview} role="status">
      <Flex align="center" gap="var(--spacing-xs)">
        <Icon name="Info" width={16} height={16} />
        <Text variant="sub" size="md" fw="500" className={styles.previewTitle}>
          {t("blackout_repeat.preview_title", { defaultValue: "What will happen" })}
        </Text>
      </Flex>
      <Text variant="caption" size="lg" className={isNone ? styles.previewWarning : styles.previewText}>
        {countLine}
      </Text>
      {preview.next.length > 1 && (
        <Text variant="caption" size="lg" className={styles.previewText}>
          <span className={styles.previewLabel}>{t("blackout_repeat.preview_next", { defaultValue: "Next:" })}</span>{" "}
          {preview.next.map(formatOccurrenceDate).join(" · ")}
          {hasMore && " · …"}
        </Text>
      )}
    </Flex>
  )
}

RepeatPreview.propTypes = {
  recurrence: PropTypes.shape({
    frequency: PropTypes.string.isRequired,
    interval: PropTypes.number,
    weekdays: PropTypes.array,
    ends_on: PropTypes.string
  }).isRequired,
  entries: PropTypes.arrayOf(PropTypes.shape({ start_date: PropTypes.string, end_date: PropTypes.string })).isRequired
}

RepeatSettings.propTypes = {
  /** null when the blackout does not repeat. */
  recurrence: PropTypes.shape({
    frequency: PropTypes.oneOf(Object.values(REPEAT_FREQUENCIES)).isRequired,
    interval: PropTypes.number,
    weekdays: PropTypes.arrayOf(PropTypes.oneOf(WEEKDAY_KEYS)).isRequired,
    ends_on: PropTypes.string
  }),
  onChange: PropTypes.func.isRequired,
  /** The "Applies on" rows — the first date is where the repeat starts. */
  entries: PropTypes.arrayOf(PropTypes.shape({ start_date: PropTypes.string })).isRequired,
  /** One of `RECURRENCE_ERRORS`, or null. */
  error: PropTypes.oneOf(Object.values(RECURRENCE_ERRORS))
}

export default RepeatSettings
